/* eslint-disable max-lines */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ButtonSize, ButtonType } from '../../../types/button.types';
import { HeightType, WidthType } from '../../../types/common.types';
import {
  ModalType,
  ModalTypeProperties
} from './models/simple-modal.interfaces';

@Component({
  selector: 'idp-simple-modal',
  templateUrl: './simple-modal.component.html',
  styleUrls: ['./simple-modal.component.scss']
})
export class SimpleModalComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  @Input() loadBackground = true; // Input for differentiating blocking and non blocking modal
  @Input() submitButtonTitle = 'Demo Title';
  @Input() showSubmitButton = false;
  @Input() submitButtonSize: ButtonSize = 'large';
  @Input() submitButtonStyle: ButtonType = 'secondary';
  @Input() cancelButtonTitle = 'Close';
  @Input() modalTitle = 'Modal Title';
  @Input() message = '';
  @Input() ariaModalLabel =
    'Dialog ' + this.modalTitle + ', press escape to close';

  @Input() subTitle = 'A Subheader';
  @Input() paragraphDescription = '';
  @Input() showFooter = true;
  @Input() closeOnEscape = true;
  @Input() closeOnBackgroundClick = true;
  @Input() cancelButtonSize: ButtonSize = 'large';
  @Input() modalStyle = 'secondary';
  @Input() headerStyle = 'dark';
  @Input() id = 'idp-dialog-name';
  @Input() idOverride = '';
  @Input() roleType = 'dialog';
  @Input() showCloseButton = true;
  @Input() showCloseIcon = true;
  @Input() showBackButton = false;
  @Input() whiteCloseIcon = false;
  @Input() type: ModalType = ModalType.Default;
  @Input() height: HeightType = 'auto';
  @Input() checkboxValue = false;
  @Input() openWindow = false;
  @Input() scrollModal = false;
  @Input() tabIndex = '';
  @Input() ariaLabelClose = '';
  @Input() ariaLabelSubmit = '';
  @Input() customClass: string = 'default' as string;
  @Input() showContinue = false;
  @Input() preventCloseModal = true;
  @Input() marginTop = '';
  @Output() openChange = new EventEmitter<boolean>();
  @Output() continueAction = new EventEmitter<boolean>();
  @Output() previousTab = new EventEmitter<Event>();
  @Output() ctaAction = new EventEmitter<Event>();
  modalType: ButtonType = 'primary';
  pageWidth = window.innerWidth;
  typeProperties: ModalTypeProperties[] = [
    {
      type: ModalType.Default,
      classname: false,
      header: true,
      focus: true,
      footer: true
    },
    {
      type: ModalType.Minimal,
      classname: true,
      header: false,
      focus: true,
      footer: false
    },
    {
      type: ModalType.Accordion,
      classname: true,
      header: false,
      focus: false,
      footer: false
    }
  ];

  displayProperties: ModalTypeProperties = {} as ModalTypeProperties;
  showAllBtn = false;
  @ViewChild('simpleModalRef') simpleModalRef!: ElementRef;

  constructor(
    private elementRef: ElementRef,
    private cdn: ChangeDetectorRef,
    private renderer: Renderer2
  ) {}

  _open = false;

  @Input() set open(value: boolean) {
    if (value !== this._open) {
      this._open = value;
      if (this._open) {
        setTimeout(() => {
          const modal =
            this.elementRef.nativeElement.querySelector('.idp-simple-modal');
          if (value && modal) {
            modal.focus();
          }
        });
        if (this.loadBackground) {
          this.setBackgroundScroll(true);
        }
      }
    }
  }

  _width: WidthType = 'normal';

  @Input() set width(value: WidthType) {
    if (this._open && value) {
      this._width = value;
      setTimeout(() => {
        const modal =
          this.elementRef.nativeElement.querySelector('.idp-dialog__body');
        if (value && modal) {
          modal.classList.add('idp-dialog__body--' + value + '-width');
        }
      });
    }
  }

  ngOnInit() {
    this.displayProperties = this.setDisplayProperties(this.type);
    this.getModalType();
    this.showAllBtn =
      this.modalStyle === 'error-details' &&
      this.showCloseButton &&
      this.showSubmitButton;
  }

  getModalType() {
    this.modalType =
      this.modalStyle === 'advisory'
        ? 'primary'
        : this.modalStyle === 'error-details'
        ? 'secondary'
        : (this.modalStyle as ButtonType);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if (changes.hasOwnProperty('type')) {
    if (changes?.['type']) {
      this.displayProperties = this.setDisplayProperties(
        changes['type'].currentValue
      );
    }
    // if (changes.hasOwnProperty('open')) {
    if (changes?.['open']) {
      if (changes['open'].currentValue) {
        this.setFocusOnModal();
      } else {
        this.setBackgroundScroll();
      }
    }
  }

  ngAfterViewInit() {
    if (this.simpleModalRef && this.simpleModalRef.nativeElement) {
      this.simpleModalRef.nativeElement.focus();
      this.cdn.detectChanges();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setDisplayProperties(type: ModalType): ModalTypeProperties {
    const result = this.typeProperties.find(
      (typeProperty: ModalTypeProperties) => typeProperty.type === this.type
    );
    if (result === undefined) {
      return {} as ModalTypeProperties;
    }
    return result;
  }

  /** get class name  for modal dialog */
  getDialogModalClass(modalStyle: string, scrollModal: boolean): string {
    let className = '';
    if (modalStyle === 'advisory') {
      className = 'advisory--content';
    } else {
      className = modalStyle;
    }
    if (scrollModal) {
      className = `${className} overflow-hidden`;
    }
    if (this.marginTop) {
      className = `${className} margin-top`;
    }
    return className;
  }

  /**
   * get the aria label for screen reader
   */
  getAriaLabel(displayProperties: ModalTypeProperties, title = ''): string {
    return displayProperties && displayProperties.focus
      ? 'Dialog ' + title + ', press escape to close'
      : '';
  }

  // eslint-disable-next-line complexity
  tabPrevious(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    /* select last button in  footer */
    const footerButtons = this.elementRef.nativeElement.querySelectorAll(
      '.idp-dialog__footer button'
    );
    const element = footerButtons[footerButtons.length - 1];
    const lastFocusElm = this.elementRef.nativeElement.querySelector(
      '.idp-upsell-modal__expand-link'
    );
    const checkboxFocus =
      lastFocusElm &&
      lastFocusElm.parentElement.previousElementSibling.querySelector(
        'input.idp-checkbox'
      );
    if (element) {
      element.focus();
    } else if (lastFocusElm && this.checkboxValue === true) {
      lastFocusElm.focus();
    } else if (lastFocusElm && checkboxFocus) {
      checkboxFocus.focus();
    } else {
      this.previousTab.emit(event);
    }
  }

  handlingEscape(event: Event) {
    if (this.closeOnEscape) {
      this.close(event);
    }
  }

  handlingTab(event: Event) {
    if (
      (event as KeyboardEvent).code === 'Tab' &&
      (<HTMLButtonElement>event.target).className ===
        "idp-btn idp-secondary idp-btn-large + '"
    ) {
      this.elementRef.nativeElement.querySelector('.idp_dialog__close').focus();
    }
  }

  outsideModal(event: Event) {
    if (this.closeOnBackgroundClick) {
      this.close(event);
    }
  }

  /**
   * Handler fot tab press in footer button
   * @param event
   */
  focus(event: Event): void {
    /* select last button in footer section */
    const footerButtons = this.elementRef.nativeElement.querySelectorAll(
      '.idp-dialog__footer button'
    );
    const lastFooterButton = footerButtons[footerButtons.length - 1];
    if (event && event.target === lastFooterButton) {
      /* if the target is the last button in footer
        prevent focus reach out of modal and focus on close button */
      event.preventDefault();
      event.stopPropagation();
      const closeButton = this.elementRef.nativeElement.querySelector(
        '.idp-dialog__header button'
      );
      if (closeButton) closeButton.focus();
      else {
        /* fallback to first focusable element if close button not available */
        this.setFirstFocusableElement();
      }
    }
    /* if not the last button go for default behaviour */
  }

  setFirstFocusableElement(): void {
    const modalContent = this.elementRef.nativeElement.querySelector(
      '.idp-dialog__content'
    );
    const focusableElements = modalContent?.querySelectorAll(
      `a[href]:not([hidden]),
      area[href]:not([hidden]),
      input:not([disabled]):not([hidden]),
      select:not([disabled]):not([hidden]),
      textarea:not([disabled]):not([hidden]),
      button:not([disabled]):not([hidden]),
      [tabindex]:not([tabindex="-1"]):not([disabled]):not([hidden])`
    );
    const uniqueElements = Array.from(
      new Set(Array.from<HTMLElement>(focusableElements || []))
    );
    const visibleElements = Array.from<HTMLElement>(
      uniqueElements || []
    ).filter((el: HTMLElement) => {
      const computedStyle = window.getComputedStyle(el);
      return (
        computedStyle.display !== 'none' &&
        computedStyle.visibility !== 'hidden'
      );
    });
    visibleElements?.[0]?.focus();
  }

  /** Responsible to close the  modal */
  close(event: Event | boolean) {
    if (event instanceof Event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this._open = this.preventCloseModal ? false : true;
    this.openChange.emit(this._open);
    this.setBackgroundScroll();
  }

  /** Responsible to close the  modal */
  continue(event: Event | boolean) {
    if (event instanceof Event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this._open = true;
    this.continueAction.emit(this._open);
    this.setBackgroundScroll();
  }

  setFocusOnModal() {
    this.cdn.detectChanges();
    if (this.simpleModalRef && this.simpleModalRef.nativeElement) {
      this.simpleModalRef.nativeElement.focus();
    }
  }

  /**
   * Set the backround scroll disabled based on the flag passed
   */
  // eslint-disable-next-line complexity
  setBackgroundScroll(set = false) {
    const body = document.querySelector('body');
    if (!body) {
      return;
    }
    if (set) {
      if (body.classList.contains('shopping-lite-app')) {
        body.classList.add('new-modal-open');
      } else {
        this.renderer.setAttribute(body, 'style', 'overflow: hidden');
      }
    } else {
      if (
        body.classList.contains('shopping-lite-app') &&
        body.classList.contains('new-modal-open')
      ) {
        body.classList.remove('new-modal-open');
      } else {
        const style = body.getAttribute('style');
        if (style && style.includes('overflow')) {
          this.renderer.removeAttribute(body, 'style');
        }
      }
    }
  }

  /** Triggers on click of secondary footer button */
  ctaEvent(event: Event | boolean) {
    this._open = this.preventCloseModal ? false : true;
    this.setBackgroundScroll();
    if (event instanceof Event) {
      this.ctaAction.emit(event);
    } else {
      this.ctaAction.emit();
    }
  }

  /**
   * Sets classes for the modal
   */
  setModalClasses() {
    let classNameAvailable = false;
    if (this.displayProperties && this.displayProperties.classname) {
      classNameAvailable = true;
    }
    return {
      'idp-dialog__content': true,
      'idp-dialog__content--minimal': classNameAvailable,
      'idp-simple-modal__scroll': this.scrollModal,
      'show-cancel-submit': this.showAllBtn
    };
  }

  ngOnDestroy(): void {
    this.setBackgroundScroll();
  }
}
